import React, { createContext, useContext, useState, useEffect, useRef } from "react";

interface BabyTimer {
  type: "feeding" | "sleep";
  side?: "L" | "R";
  startedAt: number;
  seconds: number;
}

interface BabyContextType {
  activeTimer: BabyTimer | null;
  startTimer: (type: "feeding" | "sleep", side?: "L" | "R") => void;
  stopTimer: () => BabyTimer | null;
  timerSeconds: number;
}

const BabyContext = createContext<BabyContextType>({
  activeTimer: null,
  startTimer: () => {},
  stopTimer: () => null,
  timerSeconds: 0,
});

export const useBaby = () => useContext(BabyContext);

export const BabyProvider = ({ children }: { children: React.ReactNode }) => {
  const [activeTimer, setActiveTimer] = useState<BabyTimer | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (activeTimer) {
      intervalRef.current = setInterval(() => {
        setTimerSeconds(Math.floor((Date.now() - activeTimer.startedAt) / 1000));
      }, 1000);
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setTimerSeconds(0);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [activeTimer]);

  const startTimer = (type: "feeding" | "sleep", side?: "L" | "R") => {
    const timer: BabyTimer = { type, side, startedAt: Date.now(), seconds: 0 };
    setActiveTimer(timer);
    setTimerSeconds(0);
  };

  const stopTimer = () => {
    const stopped = activeTimer ? { ...activeTimer, seconds: timerSeconds } : null;
    setActiveTimer(null);
    return stopped;
  };

  return (
    <BabyContext.Provider value={{ activeTimer, startTimer, stopTimer, timerSeconds }}>
      {children}
    </BabyContext.Provider>
  );
};

export default BabyContext;
